import { mapToElementState } from '@src/renderer/ElementState';
import { KARAOKE_TRACK_NUMBER } from '@src/stores/VideoCreatorStore';
import { useEditor } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getTrackType } from '@src/utility/trackTypes';
import { useMemo } from 'react';

import type { Element } from '@src/editor/models/Element';
import type { ElementState } from '@src/renderer/ElementState';
import type { TrackType } from '@src/utility/trackTypes';

/**
 * Converts an Element (domain model) to ElementState using mapToElementState.
 */
function toElementStateLike(element: Element): ElementState {
  return mapToElementState(element.toJSON());
}

// Row height per track type; the playhead's total height sums these too.
const TRACK_HEIGHTS: Partial<Record<TrackType, number>> = {
  'original-video': 99,
  text: 38,
  karaoke: 38,
  logo: 38,
  shape: 38,
  composition: 38,
  // PLAT-11816 — photo blocks carry the same typed preview as graphics
  image: 38,
};

export const useTimelineTracks = () => {
  const videoCreator = useVideoCreatorStore();
  const editorStore = useEditor();

  // Calculate tracks with proper ordering and filtering
  // Read from EditorStore.tracksMap (single source of truth) instead of VideoCreatorStore.tracks
  // Convert to ElementState[] for backward compatibility with consumers
  const tracks = useMemo(() => {
    const rawTracks = Array.from(editorStore.tracksMap.entries());

    // Sort by numeric track id so render order is deterministic (top = highest id)
    const sortedTracks = rawTracks.sort(([a], [b]) => b - a);

    // Convert Element[] to ElementState[] for each track
    let tracksArray: [number, ElementState[]][] = sortedTracks.map(
      ([trackNum, elements]) => [
        trackNum,
        elements.map(el => toElementStateLike(el)),
      ],
    );

    // Remove empty tracks and filter out karaoke track.
    //
    // The dub needs no filtering here any more (PLAT-11511). Its audio is built
    // into the document a player consumes and is never on the timeline the
    // editor shows, so there is nothing to hide and nothing to protect.
    tracksArray = tracksArray.filter(
      ([track, elements]) =>
        track < KARAOKE_TRACK_NUMBER && elements.length > 0,
    );

    const karaokeElements = editorStore.tracksMap.get(KARAOKE_TRACK_NUMBER);
    if (karaokeElements) {
      const videoTrack = tracksArray.findIndex(([, elements]) =>
        videoCreator.isOriginalVideoElement(elements[0].source),
      );

      tracksArray.splice(videoTrack + 1, 0, [
        KARAOKE_TRACK_NUMBER,
        karaokeElements.map(el => toElementStateLike(el)),
      ]);
    }
    return tracksArray;
  }, [editorStore.tracksMap, editorStore.editor, videoCreator]);

  // Calculate active tracks
  const activeTracks = useMemo(() => {
    return tracks
      .filter(
        ([track, elements]) =>
          elements.some(e =>
            videoCreator.timelineStore.isTimelineElementActive(e.source.id),
          ) ||
          (track === videoCreator.selectedTrack &&
            track !== KARAOKE_TRACK_NUMBER),
      )
      .map(([t]) => t);
  }, [tracks, videoCreator.activeElementIds, videoCreator.selectedTrack]);

  // Calculate total track height
  const trackHeight = useMemo(() => {
    return tracks.reduce((acc, [, elements]) => {
      const type = getTrackType(elements, videoCreator);
      if (type === 'original-video') return acc + 130;
      return acc + (TRACK_HEIGHTS[type] ?? 78);
    }, 20);
  }, [tracks, videoCreator]);

  // Get track information helper
  const getTrackInfo = (track: number, elements: ElementState[]) => {
    // Use centralized utility to determine track type
    const type = getTrackType(elements, videoCreator);
    const isActive = activeTracks.includes(track);

    const height = TRACK_HEIGHTS[type] ?? 78;

    return {
      type,
      isActive,
      height,
    };
  };

  return {
    tracks,
    activeTracks,
    trackHeight,
    getTrackInfo,
  };
};
